import { useCallback, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePendingApprovals } from '../hooks/usePendingApprovals';
import { ApprovalList } from '../components/PendingApprovals/ApprovalList';
import { LookupCollectionList } from '../components/LookupManager/LookupCollectionList';
import { UserManagement, BrandInvites, E2ESmokeTest } from '../components/UserManagement';
import { CuratedWatches } from '../components/CuratedWatches';
import { CrossBrandCalibreSearch } from '../components/CuratedWatches';
import { BulkOccasionExtractor } from '../components/CuratedWatches/BulkOccasionExtractor';
import { BulkCalibreEnricher } from '../components/CuratedWatches/BulkCalibreEnricher';
import { BulkModuleEnricher } from '../components/CuratedWatches/BulkModuleEnricher';
import { BulkDescriptionRegen } from '../components/CuratedWatches/BulkDescriptionRegen';
import { CuratedCalibres } from '../components/CuratedCalibres';
import { ElectronicModules } from '../components/ElectronicModules';
import { CuratedWinders } from '../components/CuratedWinders';
import { CalibreMovementTypeBackfill } from '../components/CalibreMovementTypeBackfill';
import { GenericPages } from '../components/GenericPages';
import { ImageAnalysisTest } from '../components/ImageAnalysis';
import { AISandbox } from '../components/AISandbox';
import { FlaggedWordsManager } from '../components/FlaggedWords';
import { ReportsManager } from '../components/Reports';
import { ArticlesConfig } from '../components/ArticlesConfig';
import { AIPromptsEditor } from '../components/Configuration';
import { CorrectionRequestsManager } from '../components/CorrectionRequests';
import { PromotedBrandsManager } from '../components/BrandPromotion';
import { CuratedCollectionsManager } from '../components/CuratedCollections';
import { ImportSourcesPage } from '../components/ImportSources';
import { ProposalsPage, recordProposalApplied } from '../components/ExternalBrandProposals';
import type { CreateBrandSeed, EditBrandPatchSeed } from '../components/CuratedWatches/brandSeeds';
import { MovementManufacturerEditor } from '../components/MovementManufacturers';
import { ParentOrganizations } from '../components/ParentOrganizations';
import { WatchChartsMapping } from '../components/WatchChartsMapping';
import {
  RnAppSwitchesTab,
  RtdbConfigsTab,
  FeatureFlagsTab,
  CatalogIndexesTab,
  DataMaintenanceTab,
  AnthropicApiTab,
  AnthropicCreditBanner,
  OpenAiUsageTab,
} from '../components/AdminConfig';
import { ExtractorConfigTab, ExtractorJobsPage } from '../components/Extractors';
import { ReferenceCleanupPage } from '../components/ReferenceCleanup';
import { SubmissionsIndexManager } from '../components/SubmissionsIndex';
import { RequestsAdminPage } from '../components/BrandPortalRequests';
import { PushNotifications } from '../components/PushNotifications';
import { EmailSender } from '../components/EmailSender';
import { BannersManager } from '../components/Banners';
import { ShareLinkGenerator } from '../components/ShareLinks';
import { BrandBannerFallbacksManager } from '../components/BrandBannerFallbacks';
import { RoadmapManager } from '../components/Roadmap';
import { useOpenEntityRequestsCount } from '../hooks/useEntityRequestsAdmin';
import { usePendingCorrectionRequestsCount } from '../hooks/useCorrectionRequestsAdmin';
import { Button } from '../components/common/Button';
import { TrademarkNotice } from '../components/common/TrademarkNotice';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

type Tab = 'approvals' | 'submissions-index' | 'brand-requests' | 'lookups' | 'curated' | 'calibre-search' | 'curated-calibres' | 'electronic-modules' | 'curated-winders' | 'movement-manufacturers' | 'parent-organizations' | 'watchcharts-mapping' | 'users' | 'invites' | 'pages' | 'flagged-words' | 'reports' | 'image-analysis' | 'articles-config' | 'configuration' | 'corrections' | 'promotions' | 'collections' | 'bulk-occasion' | 'bulk-calibre' | 'bulk-module' | 'bulk-description' | 'calibre-movement-backfill' | 'import-sources' | 'external-brand-proposals' | 'reference-cleanup' | 'rn-switches' | 'rtdb-configs' | 'brand-banner-fallbacks' | 'feature-flags' | 'catalog-indexes' | 'data-maintenance' | 'push-notifications' | 'email-sender' | 'banners' | 'share-links' | 'ai-sandbox' | 'anthropic-usage' | 'openai-usage' | 'e2e-smoke-test' | 'roadmap' | 'extractor-jobs' | 'extractor-config';
type GroupId = 'catalogue' | 'articles' | 'promoted' | 'app-feedback' | 'configuration' | 'users' | 'roadmap';

type SubTab = { tab: Tab; label: string };
type Group = { id: GroupId; label: string; defaultTab: Tab; subs: SubTab[] };

const GROUPS: Group[] = [
  {
    id: 'catalogue',
    label: 'Catalogue',
    defaultTab: 'curated',
    subs: [
      { tab: 'curated', label: 'Curated Watches' },
      { tab: 'calibre-search', label: 'Calibre Search' },
      { tab: 'curated-calibres', label: 'Curated Calibres' },
      { tab: 'electronic-modules', label: 'Electronic Modules' },
      // The tab KEY stays `curated-winders` — it is a deep-link target other
      // surfaces already point at, and renaming it would break those links for
      // a cosmetic gain. Only the label widened, to the third kind the
      // catalogue now holds.
      { tab: 'curated-winders', label: 'Curated Appliances' },
      { tab: 'movement-manufacturers', label: 'Movement Manufacturers' },
      { tab: 'parent-organizations', label: 'Parent Organizations' },
      { tab: 'watchcharts-mapping', label: 'WatchCharts Mapping' },
      { tab: 'import-sources', label: 'Import Sources' },
      { tab: 'external-brand-proposals', label: 'External Brand Proposals' },
      { tab: 'extractor-jobs', label: 'Extractor Jobs' },
      { tab: 'lookups', label: 'Lookup Collections' },
      { tab: 'calibre-movement-backfill', label: 'Calibre Movement-Type Backfill' },
      { tab: 'bulk-occasion', label: 'Occasion Tagger' },
      { tab: 'bulk-calibre', label: 'Calibre Enrichment' },
      { tab: 'bulk-module', label: 'Module Enrichment' },
      { tab: 'bulk-description', label: 'Description Rewrite' },
      { tab: 'reference-cleanup', label: 'Reference Cleanup' },
    ],
  },
  { id: 'articles', label: 'Articles', defaultTab: 'articles-config', subs: [] },
  {
    id: 'promoted',
    label: 'Promoted',
    defaultTab: 'promotions',
    subs: [
      { tab: 'promotions', label: 'Promotions' },
      { tab: 'collections', label: 'Collections' },
    ],
  },
  {
    id: 'app-feedback',
    label: 'App Feedback',
    defaultTab: 'approvals',
    subs: [
      { tab: 'approvals', label: 'Pending Approvals' },
      { tab: 'submissions-index', label: 'Submissions Index' },
      { tab: 'brand-requests', label: 'Brand Requests' },
      { tab: 'reports', label: 'Reports' },
      { tab: 'corrections', label: 'Corrections' },
    ],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    defaultTab: 'rn-switches',
    subs: [
      { tab: 'rn-switches', label: 'RN App Switches' },
      { tab: 'rtdb-configs', label: 'RTDB Configs' },
      { tab: 'brand-banner-fallbacks', label: 'Brand Banner Fallbacks' },
      { tab: 'feature-flags', label: 'Feature Flags' },
      { tab: 'catalog-indexes', label: 'Catalog Indexes & Recompute' },
      { tab: 'data-maintenance', label: 'Data Maintenance' },
      { tab: 'configuration', label: 'AI Prompts' },
      { tab: 'ai-sandbox', label: 'AI Sandbox' },
      { tab: 'extractor-config', label: 'Extractor Service' },
      { tab: 'anthropic-usage', label: 'Anthropic API' },
      { tab: 'openai-usage', label: 'OpenAI API' },
      { tab: 'pages', label: 'Pages' },
      { tab: 'flagged-words', label: 'Flagged Words' },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    defaultTab: 'users',
    subs: [
      { tab: 'users', label: 'Users' },
      { tab: 'invites', label: 'Brand Invites' },
      { tab: 'push-notifications', label: 'Push Notifications' },
      { tab: 'email-sender', label: 'Email' },
      { tab: 'banners', label: 'Banners' },
      { tab: 'share-links', label: 'Share Links' },
      { tab: 'e2e-smoke-test', label: 'E2E Signup Test' },
    ],
  },
  // Single-tab group (same shape as Articles) — the roadmap has no sub-tabs
  // until the kanban view lands.
  { id: 'roadmap', label: 'Roadmap', defaultTab: 'roadmap', subs: [] },
];

export function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const defaultTab: Tab = isAdmin ? 'approvals' : 'articles-config';
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const [importSourcesBrandId, setImportSourcesBrandId] = useState<string | undefined>(undefined);
  // Remember the brand the user opened Import Sources FROM, so we can offer a
  // "Back to brand refs" link there. Stays set while on the Import Sources tab.
  const [importOriginBrandId, setImportOriginBrandId] = useState<string | undefined>(undefined);
  // When returning from Import Sources, asks CuratedWatches to open straight on
  // this brand's references view rather than the brands list.
  const [curatedInitialBrandId, setCuratedInitialBrandId] = useState<string | undefined>(undefined);
  /**
   * Ask the references listing to open its Run scraper modal once it has
   * settled. Set alongside `curatedInitialBrandId` by the extractor config
   * screen's Open action, and cleared by the listing when it acts on it (or
   * decides it cannot) — see `ReferenceBrowser`'s `autoRunScraper`.
   */
  const [curatedRunScraper, setCuratedRunScraper] = useState(false);
  // Deep-link target from Reference Cleanup's large-cluster warning: when set,
  // switch to Curated and open this brand's edit modal (where the Dedup identity
  // setting lives).
  const [curatedEditBrandId, setCuratedEditBrandId] = useState<string | undefined>(undefined);
  // Hand-offs from External Brand Proposals: switch to Curated and open the
  // brand Create modal pre-filled (createBrandSeed), or the Edit modal on the
  // matched brand with the reviewer's ticked values overlaid
  // (editBrandPatchSeed). One-shot, like the deep links above — cleared as soon
  // as BrandEditor has taken its own copy.
  const [createBrandSeed, setCreateBrandSeed] = useState<CreateBrandSeed | null>(null);
  const [editBrandPatchSeed, setEditBrandPatchSeed] = useState<EditBrandPatchSeed | null>(null);
  // Deep-link target from the Description Audit's "Open in Calibre Editor": when
  // set, switch to Curated Calibres and auto-open this calibre's editor modal.
  const [openCalibreId, setOpenCalibreId] = useState<string | undefined>(undefined);
  // Deep-link target for the Electronic Modules editor (parallel to openCalibreId).
  const [openModuleId, setOpenModuleId] = useState<string | undefined>(undefined);
  // Deep-link target for the Curated Winders editor (parallel to openModuleId).
  const [openWinderId, setOpenWinderId] = useState<string | undefined>(undefined);
  // Deep-link target for the Movement Manufacturers editor, from the parent-org
  // Portfolio chips (parallel to openModuleId).
  const [openManufacturerId, setOpenManufacturerId] = useState<string | undefined>(undefined);
  // Stable, because the editor's deep-link effect lists it as a dependency.
  const clearOpenManufacturerId = useCallback(() => setOpenManufacturerId(undefined), []);
  // Brand the operator just started a scraper run for, so the jobs screen opens
  // filtered to it rather than to every brand's history.
  const [extractorJobsBrandId, setExtractorJobsBrandId] = useState<string | undefined>(undefined);
  // Same flag that gates the Run scraper button — the tab and the button appear
  // and disappear together, because a history screen for a feature nobody can
  // trigger is just an empty table.
  const extractorJobsEnabled = useFeatureFlag('tools.extractorJobs');
  const { pendingCount } = usePendingApprovals({ status: 'pending' });
  const openRequestsCount = useOpenEntityRequestsCount();
  const pendingCorrectionsCount = usePendingCorrectionRequestsCount();

  const groups = extractorJobsEnabled
    ? GROUPS
    : GROUPS.map((g) => ({ ...g, subs: g.subs.filter((s) => s.tab !== 'extractor-jobs') }));
  const visibleGroups = isAdmin ? groups : groups.filter((g) => g.id === 'articles');
  const activeGroup = visibleGroups.find((g) =>
    g.subs.length > 0
      ? g.subs.some((s) => s.tab === activeTab)
      : g.defaultTab === activeTab
  );

  const topTabClass = (groupId: GroupId) =>
    `px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
      activeGroup?.id === groupId
        ? 'border-brand-600 text-brand-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  const subTabClass = (tab: Tab) =>
    `px-3 py-1 text-xs font-medium rounded-full transition-colors ${
      activeTab === tab
        ? 'bg-brand-100 text-brand-700'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`;

  const handleGroupClick = (group: Group) => {
    // Any manual tab navigation drops the Import Sources brand origin, so the
    // "Back to brand refs" link only survives the programmatic brand → import flow.
    setImportOriginBrandId(undefined);
    if (group.subs.length === 0) {
      setActiveTab(group.defaultTab);
      return;
    }
    if (!group.subs.some((s) => s.tab === activeTab)) {
      setActiveTab(group.defaultTab);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">
                Watch Keeper® Admin
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{user?.email}</span>
              {!isAdmin && (
                <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full">
                  Contributor
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={logout}>
                Sign Out
              </Button>
            </div>
          </div>

          {/* Top-level tabs */}
          <nav className="flex gap-1 -mb-px">
            {visibleGroups.map((group) => (
              <button
                key={group.id}
                onClick={() => handleGroupClick(group)}
                className={topTabClass(group.id)}
              >
                {group.label}
                {group.id === 'app-feedback' && pendingCount > 0 && (
                  <span className="ml-2 bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full text-xs">
                    {pendingCount}
                  </span>
                )}
                {group.id === 'app-feedback' && openRequestsCount > 0 && (
                  <span className="ml-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs">
                    {openRequestsCount}
                  </span>
                )}
                {group.id === 'app-feedback' && pendingCorrectionsCount > 0 && (
                  <span className="ml-1 bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs">
                    {pendingCorrectionsCount}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Sub-tabs */}
        {activeGroup && activeGroup.subs.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap gap-2">
              {activeGroup.subs.map((sub) => (
                <button
                  key={sub.tab}
                  onClick={() => {
                    setImportOriginBrandId(undefined);
                    setActiveTab(sub.tab);
                  }}
                  className={subTabClass(sub.tab)}
                >
                  {sub.label}
                  {sub.tab === 'approvals' && pendingCount > 0 && (
                    <span className="ml-1.5 bg-white/70 px-1.5 rounded-full text-[10px]">
                      {pendingCount}
                    </span>
                  )}
                  {sub.tab === 'brand-requests' && openRequestsCount > 0 && (
                    <span className="ml-1.5 bg-white/70 px-1.5 rounded-full text-[10px]">
                      {openRequestsCount}
                    </span>
                  )}
                  {sub.tab === 'corrections' && pendingCorrectionsCount > 0 && (
                    <span className="ml-1.5 bg-white/70 px-1.5 rounded-full text-[10px]">
                      {pendingCorrectionsCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isAdmin && <AnthropicCreditBanner />}
        {activeTab === 'approvals' && isAdmin && <ApprovalList />}
        {activeTab === 'submissions-index' && isAdmin && <SubmissionsIndexManager />}
        {activeTab === 'brand-requests' && isAdmin && <RequestsAdminPage />}
        {activeTab === 'lookups' && isAdmin && <LookupCollectionList />}
        {activeTab === 'curated' && isAdmin && (
          <CuratedWatches
            initialBrandId={curatedInitialBrandId}
            onInitialBrandConsumed={() => setCuratedInitialBrandId(undefined)}
            autoRunScraper={curatedRunScraper}
            onAutoRunScraperConsumed={() => setCuratedRunScraper(false)}
            editBrandId={curatedEditBrandId}
            onEditBrandConsumed={() => setCuratedEditBrandId(undefined)}
            createBrandSeed={createBrandSeed}
            onCreateBrandSeedConsumed={() => setCreateBrandSeed(null)}
            onCreateBrandSeedApplied={(brandId, seed) => {
              // The write landed: mark the row and go back to the review, so
              // the reviewer carries on down the list rather than being left
              // on the brands screen.
              recordProposalApplied('created', brandId, seed);
              setActiveTab('external-brand-proposals');
            }}
            editBrandPatchSeed={editBrandPatchSeed}
            onEditBrandPatchSeedConsumed={() => setEditBrandPatchSeed(null)}
            onEditBrandPatchSeedApplied={(brandId, seed) => {
              recordProposalApplied('updated', brandId, seed);
              setActiveTab('external-brand-proposals');
            }}
            // Closed without saving: back to the brand they were reviewing,
            // with their ticks as they left them, not stranded on this tab.
            onBrandSeedCancelled={() => setActiveTab('external-brand-proposals')}
            onOpenExtractorJobs={(brandId) => {
              setExtractorJobsBrandId(brandId);
              setActiveTab('extractor-jobs');
            }}
            onOpenBulkImport={(brandId) => {
              setImportSourcesBrandId(brandId);
              setImportOriginBrandId(brandId);
              setActiveTab('import-sources');
            }}
          />
        )}
        {activeTab === 'calibre-search' && isAdmin && <CrossBrandCalibreSearch />}
        {activeTab === 'curated-calibres' && isAdmin && (
          <CuratedCalibres
            openCalibreId={openCalibreId}
            onOpenCalibreConsumed={() => setOpenCalibreId(undefined)}
          />
        )}
        {activeTab === 'electronic-modules' && isAdmin && (
          <ElectronicModules
            openModuleId={openModuleId}
            onOpenModuleConsumed={() => setOpenModuleId(undefined)}
          />
        )}
        {activeTab === 'curated-winders' && isAdmin && (
          <CuratedWinders
            openWinderId={openWinderId}
            onOpenWinderConsumed={() => setOpenWinderId(undefined)}
          />
        )}
        {activeTab === 'calibre-movement-backfill' && isAdmin && (
          <CalibreMovementTypeBackfill
            onOpenLookupManager={() => setActiveTab('lookups')}
            onOpenCalibre={(calibreId) => {
              setOpenCalibreId(calibreId);
              setActiveTab('curated-calibres');
            }}
          />
        )}
        {activeTab === 'movement-manufacturers' && isAdmin && (
          <MovementManufacturerEditor
            openManufacturerId={openManufacturerId}
            onOpenManufacturerConsumed={clearOpenManufacturerId}
          />
        )}
        {activeTab === 'parent-organizations' && isAdmin && (
          <ParentOrganizations
            onOpenBrand={(brandId) => {
              setCuratedEditBrandId(brandId);
              setActiveTab('curated');
            }}
            onOpenManufacturer={(manufacturerId) => {
              setOpenManufacturerId(manufacturerId);
              setActiveTab('movement-manufacturers');
            }}
          />
        )}
        {activeTab === 'watchcharts-mapping' && isAdmin && <WatchChartsMapping />}
        {activeTab === 'bulk-occasion' && isAdmin && <BulkOccasionExtractor />}
        {activeTab === 'bulk-calibre' && isAdmin && <BulkCalibreEnricher />}
        {activeTab === 'bulk-module' && isAdmin && <BulkModuleEnricher />}
        {activeTab === 'bulk-description' && isAdmin && <BulkDescriptionRegen />}
        {activeTab === 'reference-cleanup' && isAdmin && (
          <ReferenceCleanupPage
            onEditBrand={(brandId) => {
              setCuratedEditBrandId(brandId);
              setActiveTab('curated');
            }}
          />
        )}
        {activeTab === 'promotions' && isAdmin && <PromotedBrandsManager />}
        {activeTab === 'collections' && isAdmin && <CuratedCollectionsManager />}
        {activeTab === 'import-sources' && isAdmin && (
          <ImportSourcesPage
            initialBrandId={importSourcesBrandId}
            onBrandConsumed={() => setImportSourcesBrandId(undefined)}
            backToBrandId={importOriginBrandId}
            onBackToBrandRefs={
              importOriginBrandId
                ? () => {
                    setCuratedInitialBrandId(importOriginBrandId);
                    setImportOriginBrandId(undefined);
                    setActiveTab('curated');
                  }
                : undefined
            }
          />
        )}
        {activeTab === 'external-brand-proposals' && isAdmin && (
          <ProposalsPage
            onCreateBrand={(seed) => {
              setCreateBrandSeed(seed);
              setActiveTab('curated');
            }}
            onUpdateBrand={(seed) => {
              setEditBrandPatchSeed(seed);
              setActiveTab('curated');
            }}
          />
        )}
        {activeTab === 'extractor-jobs' && isAdmin && extractorJobsEnabled && (
          <ExtractorJobsPage
            initialBrandId={extractorJobsBrandId}
            onBrandConsumed={() => setExtractorJobsBrandId(undefined)}
            onOpenBrand={(brandId) => {
              setCuratedEditBrandId(brandId);
              setActiveTab('curated');
            }}
          />
        )}
        {activeTab === 'extractor-config' && isAdmin && (
          <ExtractorConfigTab
            onOpenBrandReferences={(brandId, options) => {
              setCuratedInitialBrandId(brandId);
              setCuratedRunScraper(options?.runScraper === true);
              setActiveTab('curated');
            }}
          />
        )}
        {activeTab === 'users' && isAdmin && <UserManagement />}
        {activeTab === 'invites' && isAdmin && <BrandInvites />}
        {activeTab === 'push-notifications' && isAdmin && <PushNotifications />}
        {activeTab === 'email-sender' && isAdmin && <EmailSender />}
        {activeTab === 'banners' && isAdmin && <BannersManager />}
        {activeTab === 'share-links' && isAdmin && <ShareLinkGenerator />}
        {activeTab === 'e2e-smoke-test' && isAdmin && <E2ESmokeTest />}
        {activeTab === 'roadmap' && isAdmin && <RoadmapManager />}
        {activeTab === 'pages' && isAdmin && <GenericPages />}
        {activeTab === 'flagged-words' && isAdmin && <FlaggedWordsManager />}
        {activeTab === 'reports' && isAdmin && <ReportsManager />}
        {activeTab === 'corrections' && isAdmin && <CorrectionRequestsManager />}
        {activeTab === 'image-analysis' && isAdmin && <ImageAnalysisTest />}
        {activeTab === 'configuration' && isAdmin && <AIPromptsEditor />}
        {activeTab === 'ai-sandbox' && isAdmin && <AISandbox />}
        {activeTab === 'rn-switches' && isAdmin && <RnAppSwitchesTab />}
        {activeTab === 'rtdb-configs' && isAdmin && <RtdbConfigsTab />}
        {activeTab === 'brand-banner-fallbacks' && isAdmin && <BrandBannerFallbacksManager />}
        {activeTab === 'feature-flags' && isAdmin && <FeatureFlagsTab />}
        {activeTab === 'catalog-indexes' && isAdmin && <CatalogIndexesTab />}
        {activeTab === 'data-maintenance' && isAdmin && (
          <DataMaintenanceTab
            onOpenCalibre={(calibreId) => {
              setOpenCalibreId(calibreId);
              setActiveTab('curated-calibres');
            }}
            onOpenBrand={(brandId) => {
              setCuratedEditBrandId(brandId);
              setActiveTab('curated');
            }}
            onOpenManufacturer={(manufacturerId) => {
              setOpenManufacturerId(manufacturerId);
              setActiveTab('movement-manufacturers');
            }}
          />
        )}
        {activeTab === 'anthropic-usage' && isAdmin && <AnthropicApiTab />}
        {activeTab === 'openai-usage' && isAdmin && <OpenAiUsageTab />}
        {activeTab === 'articles-config' && (
          <ArticlesConfig userUid={user?.uid} isAdmin={isAdmin} />
        )}
      </main>
      <TrademarkNotice className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" />
    </div>
  );
}
